import { state } from '@codesuma/baseline'
import { IDayCell } from './calendar'
import settings from './settings'
import { UNTITLED, dayKey } from './phone-events'

// The phone's Reminders, read into the app on an iPhone: the tasks someone keeps
// there, shown on their days beside Emrouz's own.
//
// Read only and read live, the way the phone's calendar is (see
// services/phone-calendar.ts): nothing is stored, every launch starts empty, and
// a reminder is changed in the Reminders app, which a tap opens. Only the ones
// with a due date — a reminder without one belongs to no day. Android has
// nothing like it on the phone to read: Google keeps its tasks on its servers.

// One reminder, as a day shows it.
export interface IPhoneReminder {
    id: string
    title: string
    time?: string // 'HH:MM'; absent when it is due on the day as a whole
    done: boolean
}

// One reminder as the plugin hands it over (TaskRemindersPlugin.swift).
interface IRawReminder {
    id: string
    title: string
    year: number
    month: number
    day: number
    hour?: number
    minute?: number
    done: boolean
}

const capacitor = () => (window as any).Capacitor
const plugin = () => capacitor()?.Plugins?.TaskReminders

// An iPhone, with a native shell new enough to carry the plugin.
const available = () => !!capacitor()?.isNativePlatform?.() && capacitor()?.getPlatform?.() === 'ios' && !!plugin()

// What has been fetched, by Gregorian month, and the day index built from it —
// the same shape the phone's calendar is held in.
const months = new Map<string, IRawReminder[]>()
let byDay: Record<string, IPhoneReminder[]> = {}
let granted = false

const monthKey = (gy: number, gm: number) => `${gy}-${gm}`
const pad = (n: number) => String(n).padStart(2, '0')

async function permitted(): Promise<boolean> {
    if (granted) return true
    granted = (await plugin()?.permission?.())?.state === 'granted'
    return granted
}

// The ones due on the day as a whole first, then by the hour, then by title, so
// a list does not reshuffle itself between two fetches of the same day.
// Returns whether the days came out different, as the phone's calendar does.
function reindex(): boolean {
    const out: Record<string, IPhoneReminder[]> = {}
    for (const list of months.values()) {
        for (const r of list) {
            const key = dayKey(r.year, r.month, r.day)
            if (!out[key]) out[key] = []
            out[key].push({
                id: r.id,
                title: r.title.trim() || UNTITLED,
                time: r.hour === undefined ? undefined : `${pad(r.hour)}:${pad(r.minute || 0)}`,
                done: r.done,
            })
        }
    }
    for (const key in out) {
        out[key].sort((a, b) => (a.time || '').localeCompare(b.time || '') || a.title.localeCompare(b.title))
    }
    const changed = JSON.stringify(out) !== JSON.stringify(byDay)
    byDay = out
    return changed
}

async function fetchMonth(key: string) {
    const [gy, gm] = key.split('-').map(Number)
    // Local midnight to local midnight, the month where the phone is. No lists
    // named reads every list.
    const res = await plugin().find({
        from: new Date(gy, gm - 1, 1).getTime(),
        to: new Date(gy, gm, 1).getTime(),
        lists: settings.get().phoneReminderLists ?? undefined,
    })
    months.set(key, (res?.reminders || []) as IRawReminder[])
}

// The reminders for every month the given cells touch, fetched once each —
// called as the month grid and the day slides build, like the phone's events.
async function ensure(cells: IDayCell[]) {
    if (!available() || !settings.get().showPhoneReminders) return
    if (!await permitted()) return

    const missing = [...new Set(cells.map(c => monthKey(c.gy, c.gm)))].filter(k => !months.has(k))
    if (!missing.length) return
    // Claimed before the fetch, so two slides asking at once fetch it once.
    for (const k of missing) months.set(k, [])

    try {
        await Promise.all(missing.map(fetchMonth))
    } catch {
        // Access withdrawn in the phone's settings while the app was open.
        // Nothing to show, but a later look should try again.
        for (const k of missing) months.delete(k)
        granted = false
        return
    }

    if (reindex()) state.set('phone-reminders', byDay)
}

// Every month held, read again where it stands, for the reason the phone's
// calendar gives (see refresh there).
async function refresh() {
    if (!months.size) return
    try {
        await Promise.all([...months.keys()].map(fetchMonth))
    } catch {
        granted = false
        invalidate()
        return
    }
    if (reindex()) state.set('phone-reminders', byDay)
}

// Synchronous, for pages built in one pass. The switch is checked here too, so
// turning it off takes the reminders away on the very next frame.
function forDay(cell: IDayCell): IPhoneReminder[] {
    if (!settings.get().showPhoneReminders) return []
    return byDay[dayKey(cell.gy, cell.gm, cell.gd)] || []
}

// The phone's Reminders lists, for the choice in settings.
async function lists(): Promise<{ id: string, name: string }[]> {
    if (!available() || !await permitted()) return []
    try {
        return ((await plugin().lists())?.lists || [])
            .map((l: any) => ({ id: String(l.id), name: String(l.name || '') }))
            .filter((l: { name: string }) => !!l.name)
    } catch {
        return []
    }
}

// Drops everything held and tells the pages, which redraw and ask again.
function invalidate() {
    months.clear()
    byDay = {}
    state.set('phone-reminders', byDay)
}

export default {
    available,

    // Asked for only when the switch is turned on, so someone who never wants
    // the feature is never prompted.
    async enable(): Promise<boolean> {
        if (!available()) return false
        granted = (await plugin().requestPermission())?.state === 'granted'
        return granted
    },

    ensure,
    forDay,
    lists,

    // The Reminders app itself, which is where a reminder is changed.
    open() {
        plugin()?.open?.()
    },

    init() {
        if (!available()) return
        // Reminders are ticked, added and moved in the Reminders app while
        // Emrouz sits in the background, so what was fetched is read again on
        // the way back in.
        capacitor()?.Plugins?.App?.addListener?.('appStateChange',
            ({ isActive }: { isActive: boolean }) => { if (isActive) refresh() })

        // The switch, and the lists ticked under it, decide what belongs.
        const reading = () => {
            const s = settings.get()
            return JSON.stringify([s.showPhoneReminders, s.phoneReminderLists])
        }
        let last = reading()
        state.on('settings', () => {
            const next = reading()
            if (next === last) return
            last = next
            invalidate()
        })
    },
}
